import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const category = categories.find((c) => c.slug === article?.category);

  const relatedArticles = article
    ? articles
        .filter((a) => a.category === article.category && a.id !== article.id)
        .slice(0, 3)
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 text-sm mb-4">您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category?.name || '分类', to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        {/* Article Header */}
        <article className="py-8 md:py-12 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs font-medium text-accent-600 bg-accent-100 px-3 py-1 rounded-full cursor-pointer hover:bg-accent-200 transition-colors duration-200"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>

              <div className="flex flex-wrap gap-1.5 mt-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<h2>/g, '<h2 class="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">')
                  .replace(/<h3>/g, '<h3 class="font-heading text-lg font-semibold text-foreground-900 mt-8 mb-3">')
                  .replace(/<p>/g, '<p class="mb-4 text-foreground-700">')
                  .replace(/<ul>/g, '<ul class="list-disc pl-5 mb-4 space-y-1">')
                  .replace(/<li>/g, '<li class="text-foreground-700">')
                  .replace(/<strong>/g, '<strong class="font-semibold text-foreground-900">'),
              }}
            />
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">{ra.date}</span>
                        <span className="text-xs text-foreground-500">{ra.readTime}</span>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            image: article.image,
            author: {
              '@type': 'Organization',
              name: '奸情调查取证全攻略',
            },
            publisher: {
              '@type': 'Organization',
              name: '奸情调查取证全攻略',
            },
            isPartOf: {
              '@type': 'WebSite',
              name: '奸情调查取证全攻略',
              url: 'https://marriagelaw.github.io/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://marriagelaw.github.io/' },
                { '@type': 'ListItem', position: 2, name: category?.name || '', item: `https://marriagelaw.github.io/category/${article.category}` },
                { '@type': 'ListItem', position: 3, name: article.title },
              ],
            },
          }),
        }}
      />
    </>
  );
}